import { Dimensions, Pressable, View } from 'react-native';
import { colors } from '@wre/infinity-question-store/theme/colors';
import Text from '@wre/infinity-question-store/components/typography/Text';
import CircledInfo from 'pupil-app/assets/svg/zest/circled-info';
import CircledExclamation from 'pupil-app/assets/svg/zest/circled-exclamation';
import useBreakpoints from '../hooks/useBreakpoints';
import { AssetSvg } from '@wre/infinity-question-store/assets/svg';
import Toast from 'react-native-toast-message';

export const toastConfig = {
  error: ({ props }: { props: { title: string; message: string; onPress?: () => void } }) => (
    <ToastContent type="error" {...props} />
  ),
  info: ({ props }: { props: { title: string; message: string; onPress?: () => void } }) => (
    <ToastContent type="info" {...props} />
  )
};

/**
 * Component that represents the content present within the Toast.
 * Called within other Toast functions and components.
 */
const ToastContent = ({
  title,
  message,
  type = 'error',
  onPress = () => Toast.hide()
}: {
  /**
   * String to display in bold at the top of the toast.
   */
  title: string;
  /**
   * String to display at the bottom of the toast.
   */
  message: string;
  /**
   * Type of toast to show - currently can either be:
   * - 'error', red toast with an exclamation mark icon;
   * - 'info', blue toast with an information icon.
   */
  type: 'error' | 'info';
  /**
   * onPress handler.
   * Optional prop, defaults to Toast.hide().
   */

  onPress?: () => void;
}) => {
  const [backgroundColor, borderColor] = (() => {
    switch (type) {
      case 'error':
        return [colors.danger, colors.fieryRose];
      case 'info':
        return [colors.infoToast, colors.infoBorder];
    }
  })();

  const { resize } = useBreakpoints();

  return (
    <Pressable
      onPress={onPress}
      style={{
        flexDirection: 'row',
        maxWidth: Dimensions.get('window').width * 0.8,
        backgroundColor,
        borderRadius: 12
      }}
    >
      <View
        style={{
          width: 52 * resize,
          alignSelf: 'center'
        }}
      >
        {type === 'error' && (
          <CircledExclamation
            width={32 * resize}
            height={32 * resize}
            fill={colors.white}
            style={{ alignSelf: 'center', margin: 10 * resize }}
          />
        )}
        {type === 'info' && (
          <CircledInfo
            width={32 * resize}
            height={32 * resize}
            fill={colors.white}
            style={{ alignSelf: 'center', margin: 10 * resize }}
          />
        )}
      </View>
      <View
        style={{
          borderLeftColor: borderColor,
          borderLeftWidth: 1,
          padding: 12 * resize,
          // The texts inside this view don't seem to take into account the width reserved for the icon to the left,
          // and are overflowing on the right. This padding visually stops the text overflowing.
          paddingRight: 62 * resize,
          maxWidth: '100%'
        }}
      >
        <Text
          variant="WRN700"
          style={{
            textAlign: 'left',
            fontSize: 22 * resize,
            lineHeight: 33 * resize,
            color: colors.white
          }}
        >
          {title}
        </Text>
        <Text
          variant="WRN400"
          style={{
            textAlign: 'left',
            fontSize: 22 * resize,
            lineHeight: 33 * resize,
            color: colors.white
          }}
        >
          {message}
        </Text>
      </View>
      <View
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          margin: 10 * resize,
          width: 22 * resize
        }}
      >
        <AssetSvg
          name={'CloseCustomizable'}
          svgProps={{ fill: colors.white }}
          height={22 * resize}
          width={22 * resize}
        />
      </View>
    </Pressable>
  );
};

/**
 * Util function to show a Toast popup.
 * Needs to be a util rather than a straight-forward React component, as this returns a Toast class from 'react-native-root-toast'.
 * */
const showToast = ({
  title,
  message,
  type = 'error',
  extraDuration = 0,
  delay = 0,
  resize = 1,
  persist = false,
  onPress
}: {
  /**
   * String to display in bold at the top of the toast.
   */
  title: string;
  /**
   * String to display at the bottom of the toast.
   */
  message: string;
  /**
   * Type of toast to show - currently can either be:
   * - 'error', red toast with an exclamation mark icon;
   * - 'info', blue toast with an information icon.
   */
  type: 'error' | 'info';
  /**
   * Milliseconds to add to the default duration of the toast. Optional prop, defaults to 0.
   */
  extraDuration?: number;
  /**
   * Milliseconds to delay showing the toast. Optional prop, defaults to 0.
   */
  delay?: number;
  /**
   * Scale to resize elements of the Toast for different devices. Optional prop, defaults to 1.
   */
  resize?: number;
  /**
   * Boolean to determine if the Toast should persist indefinitely.
   * Optional prop, defaults to false.
   */
  persist?: boolean;
  /**
   * onPress handler.
   * Optional prop, defaults to Toast.hide() once it reaches ToastContent.
   */
  onPress?: () => void;
}) => {
  const [backgroundColor, borderColor] = (() => {
    switch (type) {
      case 'error':
        return [colors.danger, colors.fieryRose];
      case 'info':
        return [colors.infoToast, colors.infoBorder];
    }
  })();

  // This is the same duration we have been using for Toasts, as defined originally by our old package:
  const DEFAULT_DURATION = 3500;

  // This is the same top offset we have been using for Toasts, as defined originally by our old package:
  const DEFAULT_TOP_OFFSET = 20;

  return Toast.show({
    type,
    autoHide: !persist,
    visibilityTime: DEFAULT_DURATION + extraDuration,
    topOffset: DEFAULT_TOP_OFFSET * resize,
    props: {
      title,
      message,
      backgroundColor,
      borderColor,
      extraDuration,
      delay,
      onPress
    }
  });
};

/**
 * Util function to show an error Toast popup.
 * */
export const showErrorToast = ({
  title,
  message,
  extraDuration = 0,
  delay = 0,
  resize = 1,
  persist = false,
  onPress
}: {
  /**
   * String to display in bold at the top of the toast.
   */
  title: string;
  /**
   * String to display at the bottom of the toast.
   */
  message: string;
  /**
   * Milliseconds to add to the default duration of the toast. Optional prop, defaults to 0.
   */
  extraDuration?: number;
  /**
   * Milliseconds to delay showing the toast. Optional prop, defaults to 0.
   */
  delay?: number;
  /**
   * Scale to resize elements of the Toast for different devices. Optional prop, defaults to 1.
   */
  resize?: number;
  /**
   * Boolean to determine if the Toast should persist indefinitely.
   * Optional prop, defaults to false.
   */
  persist?: boolean;
  /**
   * onPress handler.
   * Optional prop, defaults to Toast.hide();
   */
  onPress?: () => void;
}) => {
  return showToast({
    title,
    message,
    type: 'error',
    extraDuration,
    delay,
    resize,
    persist,
    onPress
  });
};

/**
 * Util function to show an info Toast popup.
 * */
export const showInfoToast = ({
  title,
  message,
  extraDuration = 0,
  delay = 0,
  resize = 1
}: {
  /**
   * String to display in bold at the top of the toast.
   */
  title: string;
  /**
   * String to display at the bottom of the toast.
   */
  message: string;
  /**
   * Milliseconds to add to the default duration of the toast. Optional prop, defaults to 0.
   */
  extraDuration?: number;
  /**
   * Milliseconds to delay showing the toast. Optional prop, defaults to 0.
   */
  delay?: number;
  /**
   * Scale to resize elements of the Toast for different devices. Optional prop, defaults to 1.
   */
  resize?: number;
}) => {
  return showToast({
    title,
    message,
    type: 'info',
    extraDuration,
    delay,
    resize
  });
};
